import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface GenerationRequest {
  standort_id: string;
  from: string;
  to: string;
  projektaufgabe_id?: string;
  test_run?: boolean;
}

export interface GenerationResponse {
  success: boolean;
  run_id: string;
  message: string;
}

export interface GenerationRun {
  id: string;
  standort_id: string;
  from_date: string;
  to_date: string;
  filter_projektaufgabe_id?: string;
  requested_by?: string;
  total_candidates: number;
  created_count: number;
  skipped_existing_count: number;
  errors_count: number;
  started_at: string;
  finished_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface GenerationRunItem {
  id: number;
  run_id: string;
  projektaufgabe_id: string;
  datum: string;
  action: 'created' | 'skipped' | 'error';
  message?: string;
  created_at: string;
}

export interface GenerationRunDetails {
  run: GenerationRun;
  items: GenerationRunItem[];
}

export interface Kontrolltermin {
  id: string;
  einsatznummer: number;
  standort_id: string;
  projektaufgabe_id: string;
  kunde_id: string;
  techniker_id?: string;
  soll_zeit?: number;
  monat_plan?: string;
  wochentag_plan?: string;
  einsatztyp: string;
  datum_durchfuerung: string;
  status_id: number;
  created_at: string;
  updated_at: string;
  projektaufgabe_beschreibung: string;
  periodizitaet: number;
  rrule?: string;
  custom_days?: string;
  techniker_name?: string;
  kunde_name?: string;
}

export interface AcceptResponse {
  success: boolean;
  message: string;
  total_indicators?: number;
}

@Injectable({
  providedIn: 'root'
})
export class KontrollterminGenerationService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Generate kontrolltermine for standort
   */
  generateKontrolltermine(request: GenerationRequest): Observable<GenerationResponse> {
    return this.http.post<GenerationResponse>(`${this.apiUrl}/generator/kontrolltermine`, request);
  }

  /**
   * Get generation run details
   */
  getRun(runId: string): Observable<{success: boolean; data: GenerationRunDetails}> {
    return this.http.get<{success: boolean; data: GenerationRunDetails}>(`${this.apiUrl}/generator/runs/${runId}`);
  }

  /**
   * Get kontrolltermine for standort (preview)
   */
  getKontrolltermineForStandort(standortId: string, from?: string, to?: string): Observable<{success: boolean; data: Kontrolltermin[]}> {
    const params = new HttpParams()
      .set('from', from || new Date().toISOString().split('T')[0])
      .set('to', to || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    
    return this.http.get<{success: boolean; data: Kontrolltermin[]}>(`${this.apiUrl}/generator/kontrolltermine/standort/${standortId}`, { params });
  }

  /**
   * Accept kontrolltermin
   */
  acceptKontrolltermin(kontrollterminId: string): Observable<AcceptResponse> {
    return this.http.post<AcceptResponse>(`${this.apiUrl}/kontrolltermine/${kontrollterminId}/accept`, {});
  }

  /**
   * Get kontrolltermin with snapshot info
   */
  getKontrollterminWithSnapshot(kontrollterminId: string): Observable<{success: boolean; data: any}> {
    return this.http.get<{success: boolean; data: any}>(`${this.apiUrl}/kontrolltermine/${kontrollterminId}/snapshot`);
  }

  updateKontrolltermin(kontrollterminId: string, updateData: {techniker_id: string; status_id: string}): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/kontrolltermin/${kontrollterminId}`, updateData);
  }
}
